import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { ArrowLeft, Loader2, LifeBuoy, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';
import { useAuth } from '@/context/AuthContext';
import { extractErrorMessage } from '@/lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  open:        { label: 'Open',        className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  in_progress: { label: 'In progress', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  resolved:    { label: 'Resolved',    className: 'bg-blue-100 text-blue-700 border-blue-200' },
  closed:      { label: 'Closed',      className: 'bg-slate-200 text-slate-600 border-slate-300' },
};

const CATEGORY_LABELS = {
  bug: 'Bug',
  billing: 'Billing',
  feature_request: 'Feature',
  account: 'Account',
  other: 'Other',
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso || ''; }
};

export default function AdminSupportTickets() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [status, setStatus] = useState('open');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    // Super-admin gate is server-side; this check just avoids rendering
    // the page shell for non-admins.
    if (user && user.email !== 'collin@mbscayman.com') {
      navigate('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, status, category]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (category !== 'all') params.set('category', category);
      const res = await fetch(`${API_URL}/api/admin/support/tickets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(extractErrorMessage(data, 'Could not load tickets'));
        return;
      }
      setTickets(data.tickets || []);
      setCounts(data.counts || {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead title="Support · Admin | Schedule Guru" description="Admin support ticket queue." path="/admin/support" />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="mb-2" data-testid="admin-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Admin dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-2 text-[#00247D]">
              <LifeBuoy className="w-5 h-5" />
              <h1 className="font-bold tracking-wide text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                SUPPORT TICKETS
              </h1>
            </div>
          </div>
        </div>

        {/* Status pill filter — mirrors the counts response. */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {['all', 'open', 'in_progress', 'resolved', 'closed'].map((s) => {
            const n = s === 'all'
              ? Object.values(counts).reduce((a, b) => a + b, 0)
              : (counts[s] || 0);
            const active = status === s;
            return (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                  active
                    ? 'bg-[#00247D] text-white border-[#00247D]'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`filter-status-${s}`}
              >
                {s === 'all' ? 'All' : STATUS_META[s].label}
                <span className={`ml-1.5 inline-block min-w-[18px] text-center px-1 rounded ${active ? 'bg-white/20' : 'bg-slate-100'}`}>
                  {n}
                </span>
              </button>
            );
          })}
          <div className="flex-1" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-category">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="bug">Bug</SelectItem>
              <SelectItem value="billing">Billing</SelectItem>
              <SelectItem value="feature_request">Feature request</SelectItem>
              <SelectItem value="account">Account</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : tickets.length === 0 ? (
          <Card>
            <CardContent className="py-14 text-center text-slate-500">
              No tickets match these filters.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="admin-ticket-list">
            {tickets.map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.open;
              const admin = t.last_reply_by === 'admin';
              return (
                <Link key={t.id} to={`/admin/support/${t.id}`} data-testid={`admin-ticket-row-${t.id}`}>
                  <Card className="hover:border-[#00247D]/40 hover:shadow-sm transition-all cursor-pointer">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className={`${meta.className} text-[11px]`}>{meta.label}</Badge>
                          <Badge variant="outline" className="text-[11px]">{CATEGORY_LABELS[t.category] || t.category}</Badge>
                          {!admin && (
                            <span className="text-[11px] text-amber-600 font-semibold">· awaiting reply</span>
                          )}
                        </div>
                        <div className="font-semibold text-slate-900 truncate">{t.subject}</div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                          {t.user_name || t.user_email} · updated {fmtDate(t.updated_at)}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
